import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container,
  Typography, 
  Card, 
  CardContent,
  IconButton,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EmailIcon from '@mui/icons-material/Email';

interface TeamMember {
  id: number;
  slug: string;
  name: string;
  role: string;
  image: string;
}

interface TeamCarouselProps {
  members: TeamMember[];
  currentMemberId: number;
  title?: string;
}

const CarouselContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 0),
  backgroundColor: '#f8f9fa',
  position: 'relative',
}));

const CarouselHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#455b40',
  border: '2px solid #e0e0e0',
  width: 48,
  height: 48,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    backgroundColor: '#455b40',
    color: '#ffffff',
    borderColor: '#455b40',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(69, 91, 64, 0.3)',
  },
  transition: 'all 0.3s ease',
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    color: '#bdbdbd',
    borderColor: '#e0e0e0',
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
}));

const CarouselContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
}));

const CarouselTrack = styled(Box)<{ translateX: number }>(({ theme, translateX }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  transform: `translateX(${translateX}px)`,
  transition: 'transform 0.5s ease-in-out',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
  },
}));

const MemberCard = styled(Card)(({ theme }) => ({
  minWidth: 320,
  flexShrink: 0,
  cursor: 'pointer',
  borderRadius: 0,
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  clipPath: 'url(#clip-another4)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(69, 91, 64, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 280,
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 350,
  height: 400,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
}));

const ClipPathSVG = styled('svg')(() => ({
  position: 'absolute',
  top: '-999px',
  left: '-999px',
  width: 0,
  height: 0,
}));

const MemberImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const MemberInfo = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#455b40',
  textAlign: 'center',
}));

const RoleChip = styled(Typography)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#ffffff',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '16px',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
}));

const MailIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 20,
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.15)',
  },
}));

const CardWrapper = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-block',
}));

const TeamCarousel: React.FC<TeamCarouselProps> = ({ 
  members, 
  currentMemberId, 
  title = "Otros Miembros del Equipo" 
}) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  
  // Filter out the current member
  const otherMembers = members.filter(member => member.id !== currentMemberId);
  
  const [cardsPerView, setCardsPerView] = React.useState(3);

  React.useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setCardsPerView(1);
      } else if (width < 960) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  const maxIndex = Math.max(0, otherMembers.length - cardsPerView);
  const cardWidth = 320;
  const gap = cardsPerView === 1 ? 16 : 24;
  const translateX = -currentIndex * (cardWidth + gap);

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const handleMemberClick = (slug: string) => {
    navigate(`/equipo/${slug}`);
  };

  if (otherMembers.length === 0) {
    return null;
  }

  return (
    <CarouselContainer>
      {/* Hidden SVG with clip path definition */}
      <ClipPathSVG>
        <defs>
          <clipPath id="clip-another4" clipPathUnits="objectBoundingBox">
            <path
              d="M0 0.0417599C0 0.0186966 0.0250721 0 0.056 0H0.6105C0.641428 0 0.6665 0.0186965 0.6665 0.0417599V0.148024C0.6665 0.171087 0.691572 0.189784 0.7225 0.189784H0.944C0.974928 0.189784 1 0.20848 1 0.231544V0.95824C1 0.981303 0.974928 1 0.944 1H0.056C0.0250721 1 0 0.981303 0 0.95824V0.0417599Z"
              fill="black"
            />
          </clipPath>
        </defs>
      </ClipPathSVG>
      
      <Container maxWidth="lg">
        <CarouselHeader>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: '#2c3e50',
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}
          >
            {title}
          </Typography>
          <Stack direction="row" spacing={1}>
            <NavigationButton 
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              <ArrowBackIosIcon sx={{ fontSize: '1.2rem' }} />
            </NavigationButton>
            <NavigationButton 
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
            >
              <ArrowForwardIosIcon sx={{ fontSize: '1.2rem' }} />
            </NavigationButton>
          </Stack>
        </CarouselHeader>

        <CarouselContent>
          <CarouselTrack translateX={translateX}>
            {otherMembers.map((member) => (
              <CardWrapper key={member.id}>
                <MemberCard 
                  onClick={() => handleMemberClick(member.slug)}
                >
                  <ImageContainer>
                    <MemberImage 
                      src={member.image} 
                      alt={member.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=320&h=280&fit=crop&crop=face';
                      }}
                    />
                  </ImageContainer>
                <MemberInfo>
                  <RoleChip>
                    {member.role}
                  </RoleChip>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#ffffff',
                      fontSize: '1.1rem',
                      lineHeight: 1.3,
                    }}
                  >
                    {member.name}
                  </Typography>
                </MemberInfo>
              </MemberCard>
              <MailIconContainer 
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = 'mailto:info@ekkofood.com';
                }}
              >
                <EmailIcon 
                  sx={{ 
                    fontSize: '3.5rem',
                    color: '#455b40',
                  }} 
                />
              </MailIconContainer>
              </CardWrapper>
            ))}
          </CarouselTrack>
        </CarouselContent>
      </Container>
    </CarouselContainer>
  );
};

export default TeamCarousel;